// Servei CRUD per a gestió de vehicles per usuaris
import { supabase } from '../config/supabaseClient'

// URL de l'API - utilitza variable d'entorn o localhost per defecte
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api'

/**
 * Crear un nou vehicle
 */
export const createVehicle = async (vehicleData, userId) => {
  try {
    console.log('📤 Enviando datos al backend:', { ...vehicleData, userId })

    const response = await fetch(`${API_URL}/vehicles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        year: vehicleData.year,
        price: vehicleData.price,
        description: vehicleData.description,
        kilometers: vehicleData.kilometers,
        power: vehicleData.power,
        userId: userId,
        modelId: vehicleData.modelId,
        offerTypeId: vehicleData.offerTypeId,
        fuelTypeId: vehicleData.fuelTypeId,
        images: vehicleData.images || [],
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Error al crear vehículo')
    }

    const result = await response.json()
    console.log('✅ Vehículo creado exitosamente:', result)
    return result
  } catch (error) {
    console.error('❌ Error creando vehículo:', error)
    throw error
  }
}

/**
 * Obtenir vehicles d'un usuari específic
 */
export const getUserVehicles = async userId => {
  try {
    console.log('📤 Obtenint vehicles per a user ID:', userId)

    const response = await fetch(`${API_URL}/users/${userId}/vehicles`)

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Error en obtenir vehicles')
    }

    const vehicles = await response.json()
    console.log('✅ Vehicles rebuts:', vehicles.length)

    // Formatar dades perquè coincideixin amb el format esperat per VehicleCard
    const formattedVehicles = vehicles.map(vehicle => ({
      'vehicle_id(PK)': vehicle['vehicle_id(PK)'],
      any: vehicle.any,
      preu: vehicle.preu,
      descripcio: vehicle.descripcio,
      kilometratge: vehicle.kilometratge,
      potencia: vehicle.potencia,
      brand_name: vehicle.brand_name,
      model_name: vehicle.model_name,
      offer_type_name: vehicle.offer_type_name,
      fuel_type_name: vehicle.fuel_type_name,
      images: Array.isArray(vehicle.images) ? vehicle.images.map(img => img.url).filter(url => url) : [],
    }))

    return formattedVehicles
  } catch (error) {
    console.error("❌ Error obtenint vehicles de l'usuari:", error)
    throw error
  }
}

/**
 * Actualitzar un vehicle
 */
export const updateVehicle = async (vehicleId, vehicleData, userId) => {
  try {
    // Verificar que el vehicle pertany a l'usuari
    const { data: existing, error: checkError } = await supabase.from('Vehicles').select('usuari_id').eq('vehicle_id(PK)', vehicleId).single()

    if (checkError) throw checkError
    if (existing.usuari_id !== userId) {
      throw new Error('No tens permisos per editar aquest vehicle')
    }

    // Actualitzar vehicle
    const { data, error } = await supabase
      .from('Vehicles')
      .update({
        any: vehicleData.year,
        preu: vehicleData.price,
        descripcio: vehicleData.description,
        kilometratge: vehicleData.kilometers,
        potencia: vehicleData.power,
        model_id: vehicleData.modelId,
        tipus_oferta_id: vehicleData.offerTypeId,
        tipus_combustible_id: vehicleData.fuelTypeId,
      })
      .eq('vehicle_id(PK)', vehicleId)
      .select()
      .single()

    if (error) throw error

    // Si hi ha noves imatges, eliminar les antigues i inserir les noves
    if (vehicleData.images && vehicleData.images.length > 0) {
      // Eliminar imatges antigues
      await supabase.from('Imatges').delete().eq('vehicle_id', vehicleId)

      // Inserir noves imatges
      const imageInserts = vehicleData.images.map((imageUrl, index) => ({
        vehicle_id: vehicleId,
        url_imatge: imageUrl,
        ordre_imatge: index + 1,
      }))

      await supabase.from('Imatges').insert(imageInserts)
    }

    return data
  } catch (error) {
    console.error('Error actualitzant vehicle:', error)
    throw error
  }
}

/**
 * Eliminar un vehicle
 */
export const deleteVehicle = async (vehicleId, userId) => {
  try {
    console.log('🗑️ Eliminant vehicle:', vehicleId, 'per a usuari:', userId)

    const response = await fetch(`${API_URL}/vehicles/${vehicleId}?userId=${userId}`, {
      method: 'DELETE',
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Error en eliminar vehicle')
    }

    const result = await response.json()
    console.log('✅ Vehicle eliminat:', result.message)
    return true
  } catch (error) {
    console.error('❌ Error eliminant vehicle:', error)
    throw error
  }
}

/**
 * Obtenir totes les marques des del backend
 */
export const getAllBrands = async () => {
  try {
    const response = await fetch(`${API_URL}/brands`)
    if (!response.ok) throw new Error('Error en obtenir marques')
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error obtenint marques:', error)
    throw error
  }
}

/**
 * Obtenir models d'una marca des del backend
 */
export const getModelsByBrand = async brandId => {
  try {
    const response = await fetch(`${API_URL}/brands/${brandId}/models`)
    if (!response.ok) throw new Error('Error en obtenir models')
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error obtenint models:', error)
    throw error
  }
}

/**
 * Obtenir tipus d'oferta des del backend
 */
export const getOfferTypes = async () => {
  try {
    const response = await fetch(`${API_URL}/offer-types`)
    if (!response.ok) throw new Error("Error en obtenir tipus d'oferta")
    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error obtenint tipus d'oferta:", error)
    throw error
  }
}

/**
 * Obtenir tipus de combustible des del backend
 */
export const getFuelTypes = async () => {
  try {
    const response = await fetch(`${API_URL}/fuel-types`)
    if (!response.ok) throw new Error('Error en obtenir tipus de combustible')
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error obtenint tipus de combustible:', error)
    throw error
  }
}
